import { Fetcher } from '../Fetching/Fetcher';
import { GET_SURVEY, GET_SURVEY_WITH_QUESTIONS, getPresetSurveysQuery } from '../../queries/Survey';
import { IQuestionModel } from '../Question/QuestionModel';
import { IResponseStore } from '../Response/ResponseStore';
import { IRootStore } from '../Root/RootStore';
import { ISurvey, Survey } from './SurveyModel';
import { Instance, SnapshotIn, cast, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { SurveyType } from './surveyType.enum';
import { hasError, isReady } from '../Fetching/updateStateHelper';

export const SurveyStore = Fetcher
  .named('SurveyStore')
  .props({
    survey: types.array(Survey),
    presets: types.array(types.model({
      _id: types.string,
      type: types.string,
    })),
  })
  .actions(self => {
    const _loadSurvey = flow(function*(id: string, withQuestion = false, withDynamicAnswers = false) {
      console.log('SurveyStore.loadSurvey', id, self.states.find(s => s.id === id)?.state);
      // loadSurvey needs fetched data. check if fetched data is available
      // (fetched again when the questions are wanted but were not fetched)
      const fetched = self.isFetchDone(id) ? (self.getFetchedData(id) as any) : null;
      if (!fetched || (withQuestion && !(fetched.questions && fetched.questions.length > 0))) {
        console.log('SurveyStore.loadSurvey: not done, fetching', id, self.states.find(s => s.id === id)?.state);
        yield fetchSurvey(id, withQuestion, withDynamicAnswers);
        console.log('SurveyStore.loadSurvey: fetched', id, self.states.find(s => s.id === id)?.state);
      }
      const original = self.getFetchedData(id) as any;
      self.loadFetchedData(Survey, id, partial => {
        const withQuestions = !!(original.questions && original.questions.length > 0);
        if (withQuestions) {
          const rootStore = getRoot<IRootStore>(self);
          original.questions.map(rootStore.question.loadQuestion);
        }
        const surveyType = Object.values(SurveyType).includes(partial.type as SurveyType)
          ? partial.type
          : SurveyType.unknown;
        const survey = {
          ...partial,
          type: surveyType,
          language: self.language,
          withQuestions,
        } as SnapshotIn<ISurvey>;
        const index = self.survey?.findIndex(c => c._id === survey._id);
        if (index === -1) {
          self.survey?.push(survey);
        } else {
          self.survey[index] = cast(survey);
        }
      });
    });
    const loadSurvey = function(id: string, withQuestion = false, withDynamicAnswers = false) {
      return self._deduplicate(id, () => _loadSurvey(id, withQuestion, withDynamicAnswers));
    };

    const fetchSurvey = flow(function* (id: string, withQuestion = false, withDynamicAnswers = false) {
      self.setFetchPending(id);

      const SURVEY_QUERY = withQuestion ? GET_SURVEY_WITH_QUESTIONS : GET_SURVEY;
      const response = yield self.client.query({
        query: SURVEY_QUERY,
        variables: {
          id,
          withDynamicAnswers,
        }
      });
      if (response.errors) {
        self.setFetchError(id, response.errors[0].message);
      } else {
        const survey = response.data.survey;
        self.setFetchDone(id, survey);
      }
    });
    const refetchDynamicAnswers = flow(function* (id: string) {
      const { question: qstore } = getRoot<any>(self);
      const questions = qstore.getQuestionsOfSurvey(id) as IQuestionModel[];
      const promises: Promise<void>[] = [];
      for (const q of questions) {
        if (q.answersAreDynamic) {
          promises.push(qstore.fetchQuestion({id: q._id, withDynamicAnswers: true}));
        }
      }
      yield Promise.all(promises);
    });
    const fetchSurveyIfNotExist = flow(function* (id: string, withQuestion = false, withDynamicAnswers = false) {
      if (self.survey) {
        const index = self.survey.findIndex(p => p._id === id);
        if (index === -1 || self.survey[index].language !== (getParent(self) as any).language.name.key || (!self.survey[index].withQuestions && withQuestion)) {
          yield fetchSurvey(id, withQuestion, withDynamicAnswers);
        } else {
          yield refetchDynamicAnswers(id);
        }
      } else {
        yield fetchSurvey(id, withQuestion, withDynamicAnswers);
      }
    });

    const fetchSpecialSurveys = flow(function* () {
      const { data }: {data: {surveys: {items: {type: string, _id: string}[]}}} =
      yield self.client.query({query: getPresetSurveysQuery()});
      self.presets = cast(data.surveys.items);
    });


    const clear = () => {
      self.survey = cast([]);
      self.clearFetchStates();
      self.presets = cast([]);
    };
    return {
      loadSurvey,
      fetchSurvey,
      fetchSurveyIfNotExist,
      fetchSpecialSurveys,
      refetchDynamicAnswers,
      clear
    };
  })
  .views(self => ({
    getSurvey(id: string): ISurvey | null {
      const survey = self.survey?.find(s => s._id === id);
      return survey ? survey : null;
    },
    getPresetSurveyId(type: string): string | null {
      const preset = self.presets.find(p => p.type === type);
      return preset ? preset._id : null;
    },
    getSurveyResponses(id: string) {
      const rootStore = getRoot<any>(self);
      return (rootStore.responses as IResponseStore).getResponsesForSurvey(id);
    },
    getSurveyName(id: string) {
      const survey = this.getSurvey(id);
      if (survey) {
        return survey.title;
      } else {
        return null;
      }

    },
    hasError(id: string) {
      return hasError(self, id);
    },
    hasErrorPlural(id: string[]) {
      return id.every(i => hasError(self, i));
    },
    isReady(id: string) {
      return isReady(self, id);
    },
    isReadyPlural(id: string[]) {
      return id.every(i => isReady(self, i));
    },
    isSurveyUsingNewDesign(surveyId: string): boolean {
      const survey = this.getSurvey(surveyId);
      return survey?.type === 'wpoInstantHelp';
    },
    get matchingSurvey(): string | undefined {
      const survey = self.presets.find(item => item.type === 'matching');
      return survey?._id ? survey._id : undefined;
    },
    get instantHelpSurvey(): string | undefined {
      const survey = self.presets.find(item => item.type === 'wpoInstantHelp');
      return survey?._id ? survey._id : undefined;
    },
    get wpoSchedulingSurvey(): string | undefined {
      const survey = self.presets.find(item => item.type === 'wpoScheduling');
      return survey?._id ? survey._id : undefined;
    },

  }));

export type ISurveyStore = Instance<typeof SurveyStore>;
